import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import Spinner from '~/components/Common/Spinner';

import { useGetAgency } from '~/hooks/apis/agent/useGetAgency';
import { useListAgentHubUsers } from '~/hooks/apis/agent/useListAgentHubUsers';
import { useUpdateAgency } from '~/hooks/apis/agent/useUpdateAgency';

import { Affiliation, Agency } from '~/types/services/agentHub';

import AgencyDetails from './AgencyDetails';
import AgencyEditor from './AgencyEditor';

const DEFAULT_SIZE_PER_PAGE = 10;

interface Props {
  id: string;
  onClose?: () => void;
  onUpdate?: () => void;
}

export default function AgencyDetailsModal({ id, onClose, onUpdate }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState<number>(0);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedAffiliationInEditor, setSelectedAffiliationInEditor] = useState<Affiliation | null>(null);

  const {
    data: agencyData,
    isFetching: isLoadingAgency,
    isSuccess: isAgencySuccess,
  } = useGetAgency(id);

  const { data: { rows: users = [], total = 0 } = {}, isFetching: isLoadingUsers } = useListAgentHubUsers(
    {
      agencyId: id,
      page: page.toString(),
      pageSize: DEFAULT_SIZE_PER_PAGE.toString(),
    },
  );

  const updateAgencyMutation = useUpdateAgency({
    onSuccess: () => {
      enqueueSnackbar('Register updated successfully');
      setIsEditing(false);
      onUpdate?.();
    },
    onError: () => {
      enqueueSnackbar('There was an error updating the agency', { variant: 'error' });
    },
  });

  const onSave = useCallback(
    (editedData: Partial<Agency>) => {
      if (Object.entries(editedData).some(([_, value]) => !value)) {
        enqueueSnackbar('Please fill all the fields', { variant: 'error' });
        return;
      }

      const { id, name, businessCodeValue, address, city, state, postcode, country, affiliationId } = editedData;

      if (!affiliationId) {
        enqueueSnackbar('Please select an affiliation', { variant: 'error' });
        return;
      }

      updateAgencyMutation.mutate({
        agencyId: id,
        data: {
          name,
          businessCodeValue,
          address,
          city,
          state,
          postcode,
          country,
          affiliationId,
        },
      });
    },
    [enqueueSnackbar, updateAgencyMutation],
  );

  const onCancel = useCallback(() => {
    setIsEditing(false);
    setSelectedAffiliationInEditor(null);
  }, []);

  const closeModal = useCallback(() => {
    setIsEditing(false);
    setSelectedAffiliationInEditor(null);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    setIsEditing(false);
    setSelectedAffiliationInEditor(null);
    setPage(0);
  }, [id]);

  const isLoading = isLoadingAgency;
  const loadingUserState: Utils.FetchingState = isLoadingUsers ? 'loading' : 'success';
  const isSaving = updateAgencyMutation.isPending;

  const handleSelectedAffiliationChange = useCallback((affiliation: Affiliation | null) => {
    setSelectedAffiliationInEditor(affiliation);
  }, []);

  const showAffiliationChangeWarning = useMemo(
    () =>
      !!selectedAffiliationInEditor &&
      !!agencyData?.affiliationId &&
      selectedAffiliationInEditor.id !== agencyData.affiliationId,
    [agencyData?.affiliationId, selectedAffiliationInEditor],
  );

  return (
    <Dialog onClose={closeModal} open>
      <DialogTitle>Agency Details</DialogTitle>

      <DialogContent>
        {isLoading && <Spinner />}
        {isAgencySuccess && isEditing && agencyData && (
          <>
            {showAffiliationChangeWarning && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Warning: changing an agency&apos;s affiliation can be dangerous. It may affect governance, commissions,
                reporting, and how the agency is supported. This is especially true when moving to or from managed
                affiliations. Only save if you understand the impact.
              </Alert>
            )}
            <AgencyEditor
              data={agencyData}
              onSelectedAffiliationChange={handleSelectedAffiliationChange}
              onSave={onSave}
              onCancel={onCancel}
              loadingState={isSaving ? 'loading' : 'success'}
            />
          </>
        )}
        {isAgencySuccess && !isEditing && (
          <AgencyDetails
            data={agencyData}
            users={users}
            page={page}
            setPage={setPage}
            total={total}
            loadingUserState={loadingUserState}
          />
        )}
      </DialogContent>

      <DialogActions>
        {!isEditing && (
          <Button variant="text" onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        )}
        <Button variant="text" onClick={closeModal}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
